import React, { useState, useMemo, useEffect } from "react";
import { Group, Button, Paper, Stack, Text, Box, Loader } from "@mantine/core";
import {
  IconDeviceDesktop,
  IconDeviceMobile,
  IconExternalLink,
} from "@tabler/icons-react";
import { useTemplate } from "../../../context/TemplateContext";
import { buildPreviewUrl } from "../utils/previewUrlUtils";
import { sanitizePreviewHtml } from "../utils/sanitizePreviewHtml";
import styles from "./WebsiteGeneratorPresentator.module.css";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BACKGROUND_WHITE = "#ffffff";

interface HeroContent {
  title?: string;
  description?: string;
  body?: string;
  photos?: Array<{ url?: string; src?: string; filename?: string }>;
  includeBookingButton?: boolean;
  heroBookingCopy?: string;
}

interface ServicesContent {
  title?: string;
}
interface TeamContent {
  title?: string;
  body?: string;
}
interface ContactContent {
  showDirections?: boolean;
  directions?: string;
  enableForm?: boolean;
  formEmail?: string;
  showMap?: boolean;
}
interface FooterContent {
  showSocialIcons?: boolean;
  instagram?: string;
  facebook?: string;
  twitter?: string;
  tiktok?: string;
}
// Optional new sections (kept in props for feature parity)
interface GalleryItem {
  url?: string;
  src?: string;
  filename?: string;
}
interface TestimonialItem {
  name: string;
  testimonial: string;
}

interface WebsiteGeneratorPresentatorProps {
  selectedTheme: string;
  headingFont: string;
  bodyFont: string;
  backgroundColor: string; // not used in iframe mode but preserved
  textColor: string; // not used in iframe mode but preserved
  heroContent?: HeroContent;
  servicesContent?: ServicesContent;
  teamContent?: TeamContent;
  contactContent?: ContactContent;
  footerContent?: FooterContent;
  // New preview props
  previewHtml: string | null;
  isPreviewLoading: boolean;
  previewError: string | null;
  onRetryPreview: () => void;
  // Additional content sections (optional, kept for parity)
  galleryContent?: GalleryItem[];
  testimonialsContent?: TestimonialItem[];
}

const WebsiteGeneratorPresentator: React.FC<
  WebsiteGeneratorPresentatorProps
> = ({
  selectedTheme,
  headingFont,
  bodyFont,
  backgroundColor,
  textColor,
  heroContent,
  servicesContent,
  teamContent,
  contactContent,
  footerContent,
  previewHtml,
  isPreviewLoading,
  previewError,
  onRetryPreview,
  galleryContent,
  testimonialsContent,
}) => {
  const { draftSubdomain, refreshDraftSubdomain } = useTemplate();
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");

  // Fetch draft subdomain when component mounts (lazy loading)
  useEffect(() => {
    refreshDraftSubdomain();
  }, [refreshDraftSubdomain]);

  // Sanitize HTML to prevent navigation from affecting parent window
  const sanitizedHtml = useMemo(() => {
    return sanitizePreviewHtml(previewHtml);
  }, [previewHtml]);

  // Determine body content
  let body: React.ReactNode;
  // Show loading spinner only when actively loading
  if (isPreviewLoading) {
    body = (
      <Box
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1000,
        }}
      >
        <Loader size={50} />
      </Box>
    );
  } else if (previewError || !previewHtml) {
    body = (
      <Stack
        align="center"
        justify="center"
        style={{ height: "100%", width: "100%" }}
      >
        <Text c="red" fw={500}>
          {previewError || "Preview not available"}
        </Text>
        <Button onClick={onRetryPreview} mt="md">
          Try Again
        </Button>
      </Stack>
    );
  } else {
    // Render iframe based on view mode
    if (viewMode === "mobile") {
      body = (
        <div className={styles.mobileViewportWrapper}>
          <iframe
            srcDoc={sanitizedHtml}
            title="Website Preview"
            className={`${styles.iframe} ${styles.iframeMobile}`}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      );
    } else {
      body = (
        <iframe
          srcDoc={sanitizedHtml}
          title="Website Preview"
          className={`${styles.iframe} ${styles.iframeDesktop}`}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            overflow: "auto",
          }}
          sandbox="allow-scripts allow-same-origin"
        />
      );
    }
  }

  return (
    <Stack gap="md" style={{ height: "100%" }}>
      {/* View Selector */}
      <Group justify="space-between" p="sm">
        <Group gap="xs">
          <Button
            variant={viewMode === "desktop" ? "filled" : "outline"}
            color="gray"
            size="sm"
            leftSection={<IconDeviceDesktop size={16} />}
            onClick={() => setViewMode("desktop")}
          >
            Desktop
          </Button>
          <Button
            variant={viewMode === "mobile" ? "filled" : "outline"}
            color="gray"
            size="sm"
            leftSection={<IconDeviceMobile size={16} />}
            onClick={() => setViewMode("mobile")}
          >
            Mobile
          </Button>
        </Group>
        <Button
          variant="subtle"
          color="blue"
          rightSection={<IconExternalLink size={16} />}
          disabled={!draftSubdomain}
          onClick={() => {
            if (draftSubdomain) {
              const previewUrl = buildPreviewUrl(draftSubdomain);
              window.open(previewUrl, "_blank");
            }
          }}
        >
          Preview
        </Button>
      </Group>

      {/* Website Preview (iframe only) */}
      <Paper
        p={0}
        className={
          viewMode === "mobile"
            ? styles.mobileViewContainer
            : styles.desktopViewContainer
        }
        style={{
          flex: 1,
          backgroundColor: COLOR_BACKGROUND_WHITE,
          border: `2px solid ${COLOR_BORDER_LIGHT}`,
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: viewMode === "mobile" ? "center" : "flex-start",
          overflow: viewMode === "mobile" ? "auto" : "hidden",
          position: "relative",
        }}
      >
        {body}
      </Paper>
    </Stack>
  );
};

export default WebsiteGeneratorPresentator;
